import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Heart, Brain, Sparkles, Users, Activity, Sun, Compass, Globe2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const KalyvDetail = () => {
  const values = [
    {
      icon: <Sparkles size={40} />,
      title: 'Wholeness over quick fixes',
      description: 'We focus on sustainable, lasting transformation in both physical and mental health.'
    },
    {
      icon: <Heart size={40} />,
      title: 'Innovation with empathy',
      description: 'Every solution we build is grounded in real human experience and backed by modern research.'
    },
    {
      icon: <Users size={40} />,
      title: 'Individual + Community',
      description: 'Personal well-being and social well-being are mirrors of each other; when one thrives, the other flourishes.'
    },
    {
      icon: <Compass size={40} />,
      title: 'Rooted, yet future-facing',
      description: 'Inspired by the timeless wisdom of Indian culture, powered by next-generation health innovations.'
    }
  ];

  const pillars = [
    {
      icon: <Activity size={32} />,
      title: 'Physical Vitality',
      description: 'Accessible, science-backed pathways to strength, energy, and longevity.'
    },
    {
      icon: <Brain size={32} />,
      title: 'Mental Clarity',
      description: 'Care that destigmatizes mental health and supports emotional resilience.'
    },
    {
      icon: <Sun size={32} />,
      title: 'Emotional Wellness',
      description: 'Daily practices that nurture self-awareness, balance, and purpose.'
    },
    {
      icon: <Globe2 size={32} />,
      title: 'Community Health',
      description: 'Programs that recognize the well-being of society as inseparable from the individual.'
    }
  ];

  return (
    <div data-testid="kalyv-detail-page" style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #1a0d2e 0%, #2d1b4e 50%, #3d2766 100%)',
          padding: '100px 7.6923%',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1560233026-ad254fa8da38?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15
          }} />

          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <Link to="/" data-testid="kalyv-back-link" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '40px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to Solutions
            </Link>

            <div style={{ maxWidth: '900px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(196, 154, 255, 0.15)',
                border: '1px solid #c49aff',
                marginBottom: '30px',
                fontSize: '14px',
                color: '#e0c8ff',
                fontWeight: '500',
                letterSpacing: '2px'
              }}>
                A VDM BUSINESS INNOVATIONS INITIATIVE
              </div>
              <div style={{
                background: '#ffffff',
                display: 'inline-block',
                padding: '24px 40px',
                marginBottom: '30px',
                borderRadius: '4px'
              }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_fd78a43a-475d-4d4e-ba79-642a920f6f2b/artifacts/0qvy7y6q_generated-image%20%2843%29.png"
                  alt="KALYV"
                  style={{ height: '180px', objectFit: 'contain', display: 'block' }}
                  data-testid="kalyv-hero-logo"
                />
              </div>
              <p style={{
                fontSize: 'clamp(18px, 2vw, 24px)',
                color: '#e0c8ff',
                marginBottom: '30px',
                fontStyle: 'italic',
                letterSpacing: '1px'
              }}>
                Live Completely. Live Purposefully.
              </p>
              <p className="body-large" style={{ color: 'rgba(255,255,255,0.85)', maxWidth: '780px', lineHeight: '1.8' }}>
                A new-age wellness brand born from the belief that true living is an art — and every individual deserves to master it.
              </p>
            </div>
          </div>
        </section>

        {/* The Name, The Promise */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{
              padding: '60px',
              background: 'rgba(196, 154, 255, 0.05)',
              border: '1px solid rgba(196, 154, 255, 0.4)'
            }}>
              <h2 className="display-medium" style={{ marginBottom: '24px' }}>
                The Name, <span style={{ color: '#c49aff' }}>The Promise</span>
              </h2>
              <p className="body-large" style={{ lineHeight: '1.9', marginBottom: '20px' }}>
                <strong style={{ color: '#c49aff' }}>Kala</strong> — a Sanskrit word that holds within it the essence of time, art, and completeness.
                <strong style={{ color: '#c49aff' }}> Live</strong> — the most primal human urge: to exist fully, consciously, purposefully.
              </p>
              <p className="body-large" style={{ lineHeight: '1.9' }}>
                Together, they become <strong style={{ color: '#c49aff' }}>KALYV</strong> <em>(Kah-live)</em> — a new-age wellness brand born from the belief that true living is an art, and that every individual deserves to master it.
              </p>
            </div>
          </div>
        </section>

        {/* Who We Are */}
        <section style={{ background: 'var(--bg-primary)', padding: '40px 7.6923% 80px' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }} className="kalyv-grid">
              <div>
                <h2 className="display-medium" style={{ marginBottom: '24px' }}>
                  Who <span style={{ color: '#c49aff' }}>We Are</span>
                </h2>
                <p className="body-medium" style={{ lineHeight: '1.9', marginBottom: '20px' }}>
                  KALYV is a wellness innovation division of <strong>VDM Business Innovations</strong>, dedicated to transforming the way individuals and communities experience health — not as the absence of illness, but as the fullness of life.
                </p>
                <p className="body-medium" style={{ lineHeight: '1.9', marginBottom: '20px' }}>
                  We exist at the intersection of science and soul, where cutting-edge innovation meets deeply human understanding.
                </p>
                <p className="body-medium" style={{ lineHeight: '1.9' }}>
                  We believe well-being is not a destination. It is a daily, evolving practice — physical, mental, and emotional. KALYV is built to walk that journey with you.
                </p>
              </div>
              <div>
                <img
                  src="https://images.unsplash.com/photo-1758274538961-fe8f1f24166f?w=1200"
                  alt="KALYV wellness"
                  style={{
                    width: '100%',
                    height: '500px',
                    objectFit: 'cover',
                    border: '1px solid rgba(196, 154, 255, 0.3)'
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Our Purpose */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="display-medium" style={{ marginBottom: '30px' }}>
              Our <span style={{ color: '#c49aff' }}>Purpose</span>
            </h2>
            <p className="body-large" style={{ lineHeight: '1.9', marginBottom: '24px' }}>
              At KALYV, we see a world where mental health carries no stigma, physical vitality is accessible to all, and the well-being of the individual is inseparable from the well-being of society.
            </p>
            <p className="body-large" style={{ lineHeight: '1.9', marginBottom: '24px', color: 'var(--text-muted)' }}>
              Our purpose is to close the gap between where people are and where they deserve to be — in their body, their mind, and their sense of self.
            </p>
            <p className="body-large" style={{
              lineHeight: '1.9',
              fontStyle: 'italic',
              color: '#c49aff',
              fontSize: '22px'
            }}>
              We don&apos;t just address symptoms. We redesign the experience of being well.
            </p>
          </div>
        </section>

        {/* Pillars */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Pillars of <span style={{ color: '#c49aff' }}>Well-Being</span>
              </h2>
              <p className="body-medium" style={{ maxWidth: '750px', margin: '0 auto', color: 'var(--text-muted)' }}>
                A holistic framework that recognizes well-being as a multi-dimensional, lived experience.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '24px'
            }}>
              {pillars.map((pillar, index) => (
                <div key={index} style={{
                  padding: '36px 28px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  transition: 'all 0.4s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#c49aff';
                  e.currentTarget.style.transform = 'translateY(-6px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ color: '#c49aff', marginBottom: '20px' }}>
                    {pillar.icon}
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '12px' }}>{pillar.title}</h3>
                  <p className="body-small" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>{pillar.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Stand For */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                What We <span style={{ color: '#c49aff' }}>Stand For</span>
              </h2>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '30px'
            }}>
              {values.map((value, index) => (
                <div key={index} style={{
                  padding: '40px',
                  background: 'rgba(196, 154, 255, 0.05)',
                  border: '1px solid rgba(196, 154, 255, 0.3)',
                  transition: 'all 0.4s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#c49aff';
                  e.currentTarget.style.background = 'rgba(196, 154, 255, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(196, 154, 255, 0.3)';
                  e.currentTarget.style.background = 'rgba(196, 154, 255, 0.05)';
                }}
                >
                  <div style={{ color: '#c49aff', marginBottom: '20px' }}>
                    {value.icon}
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '14px' }}>{value.title}</h3>
                  <p className="body-medium" style={{ color: 'var(--text-muted)', lineHeight: '1.8' }}>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Road Ahead */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{
              padding: '60px',
              background: 'linear-gradient(135deg, rgba(196, 154, 255, 0.08) 0%, rgba(196, 154, 255, 0.03) 100%)',
              border: '1px solid rgba(196, 154, 255, 0.4)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <h2 className="display-medium" style={{ marginBottom: '30px' }}>
                The <span style={{ color: '#c49aff' }}>Road Ahead</span>
              </h2>
              <p className="body-large" style={{ lineHeight: '1.9', marginBottom: '20px' }}>
                KALYV is at the beginning of a bold journey. In the months and years to come, we will bring to life a portfolio of innovations — products, programs, platforms, and partnerships — each designed to make well-being more intelligent, more personal, and more accessible.
              </p>
              <p className="body-large" style={{ lineHeight: '1.9', marginBottom: '30px' }}>
                Whether you are an individual seeking clarity and strength, or an organization committed to the health of its people, KALYV will be your partner in purposeful living.
              </p>
              <div style={{
                paddingTop: '24px',
                borderTop: '1px solid rgba(196, 154, 255, 0.3)'
              }}>
                <p style={{
                  fontSize: '20px',
                  fontStyle: 'italic',
                  color: '#c49aff',
                  lineHeight: '1.8'
                }}>
                  This is just the beginning. The art of living fully — starts here.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              padding: '80px 60px',
              background: 'linear-gradient(135deg, #1a0d2e 0%, #3d2766 100%)',
              border: '2px solid #c49aff',
              textAlign: 'center'
            }}>
              <h2 className="display-medium" style={{ marginBottom: '20px', color: '#ffffff' }}>
                KALYV | <span style={{ color: '#c49aff' }}>Live Completely. Live Purposefully.</span>
              </h2>
              <p className="body-large" style={{ marginBottom: '40px', maxWidth: '780px', margin: '0 auto 40px', color: 'rgba(255,255,255,0.85)' }}>
                Partner with us as we redesign the experience of being well — for individuals, for communities, for the future.
              </p>
              <Link
                to="/#contact"
                data-testid="kalyv-cta-contact"
                className="btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}
              >
                Get In Touch
                <Heart size={20} />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .kalyv-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default KalyvDetail;
